/**
 * PATH: packages/email-hub/src/feature/reader-header/placements/PlacementManagerPanel.tsx
 * FILE_ID: EMAILHUB-READERHEADER-PLACEMENTS-PANEL-001
 * PURPOSE: The Placement Manager dialog: every active placement with metadata,
 *   search/filter above 5 rows, add/link (new card or existing card cascade),
 *   409-opens-existing, stale-row removal through the action runtime,
 *   designate-primary, and changed-elsewhere refresh.
 * ROLE IN ARCHITECTURE: Overlay frame under ReaderPlacementsControl.
 * MAIN EXPORTS: PlacementManagerPanel.
 * NON-RESPONSIBILITIES: At-rest chip rendering (ReaderPlacementsControl);
 *   healthy-row verbs (kanban-host AllocationChip inside PlacementRow).
 * NOTES FOR FUTURE AI: The changed-elsewhere snapshot is taken when the
 *   manager MOUNTS: a row whose version token changes while the manager is
 *   open (sibling tab / reconcile) is flagged until the operator refreshes.
 *   Cross-org move confirm: see moveRequiresCrossOrgConfirm — structurally
 *   unreachable today because pickers are org-scoped fail-closed.
 */
import { useCallback, useMemo, useState } from 'react'
import { Search, X } from 'lucide-react'
import { AllocateChip, type getSamKanbanApi } from '@quotech/kanban-host'
import type { EmailAllocationSummary } from '@quotech/tenant-shared/email/api'

import type { Email } from '../../hooks/useEmails'
import { getReaderAction } from '../actions/registry'
import type { ReaderHeaderSurface } from '../model/types'
import { selectPrimaryPlacement } from '../model/placements'
import { useReaderActionRunner } from '../runtime/useReaderActionRunner'
import {
  PLACEMENT_FILTER_THRESHOLD,
  filterPlacements,
  findChangedPlacements,
  snapshotPlacementTokens,
} from './placementModel'
import { setPrimaryOverride } from './primaryOverride'
import { PlacementRow } from './PlacementRow'

export interface PlacementManagerPanelProps {
  email: Email
  placements: EmailAllocationSummary[]
  primary: EmailAllocationSummary | null
  overrideId: string | null
  api: ReturnType<typeof getSamKanbanApi>
  orgId: number | null
  /** Telemetry surface label; the manager renders identically on both. */
  surface?: ReaderHeaderSurface
  onClose: () => void
  /** Successful allocate/link — control owns the summaries patch. */
  onAllocated: (summary?: EmailAllocationSummary) => void
  onPatchEmail?: (emailId: string, patch: Partial<Email>) => void
  onChanged?: () => void
  onOpenCard?: (cardId: string) => void
  onOpenBoard?: (placement: EmailAllocationSummary) => void
}

export function PlacementManagerPanel({
  email,
  placements,
  primary,
  overrideId,
  api,
  orgId,
  surface = 'preview',
  onClose,
  onAllocated,
  onPatchEmail,
  onChanged,
  onOpenCard,
  onOpenBoard,
}: PlacementManagerPanelProps) {
  const runner = useReaderActionRunner({ surface, reconcile: onChanged })
  const manageAction = getReaderAction('manage_placements')

  const [query, setQuery] = useState('')
  const searchable = placements.length > PLACEMENT_FILTER_THRESHOLD
  const visible = useMemo(
    () =>
      searchable
        ? filterPlacements(placements, query, email.organisation_name)
        : [...placements],
    [searchable, placements, query, email.organisation_name],
  )

  // Concurrency watch: tokens as of manager open; rows that change while the
  // manager is open flag "Changed elsewhere" until refreshed.
  const [snapshot, setSnapshot] = useState(() =>
    snapshotPlacementTokens(placements),
  )
  const changedIds = useMemo(
    () => findChangedPlacements(snapshot, placements),
    [snapshot, placements],
  )
  const refreshRows = useCallback(() => {
    setSnapshot(snapshotPlacementTokens(placements))
    onChanged?.()
  }, [placements, onChanged])

  const removeStale = useCallback(
    (placement: EmailAllocationSummary) => {
      const next = placements.filter(
        (p) => p.allocation_id !== placement.allocation_id,
      )
      const prior = {
        allocation_summary: email.allocation_summary,
        allocation_summaries: email.allocation_summaries,
      }
      void runner.run({
        action: {
          ...manageAction,
          busyKey: `context:placements:${placement.allocation_id}`,
        },
        optimistic: onPatchEmail
          ? {
              apply: () =>
                onPatchEmail(email.id, {
                  allocation_summary: selectPrimaryPlacement(next, overrideId),
                  allocation_summaries: next,
                }),
              rollback: () => onPatchEmail(email.id, prior),
            }
          : undefined,
        execute: () =>
          api.revokeAllocation(
            placement.allocation_id,
            'Removed stale placement (card deleted or archived)',
          ),
        successToast: 'Stale placement removed',
      })
    },
    [placements, email, runner, manageAction, onPatchEmail, overrideId, api],
  )

  const designatePrimary = useCallback(
    (placement: EmailAllocationSummary) => {
      runner.runLocal(manageAction, () =>
        setPrimaryOverride(
          email.id,
          overrideId === placement.allocation_id
            ? null
            : placement.allocation_id,
        ),
      )
    },
    [runner, manageAction, email.id, overrideId],
  )

  return (
    <>
      <button
        type="button"
        className="fixed inset-0 z-[70] cursor-default bg-black/35"
        aria-label="Close Placement Manager"
        onClick={onClose}
      />
      <section
        role="dialog"
        aria-modal="true"
        aria-label="Placement Manager"
        className="fixed inset-x-3 bottom-3 z-[71] max-h-[82vh] overflow-hidden rounded-2xl border border-border bg-popover shadow-2xl sm:inset-auto sm:left-1/2 sm:top-1/2 sm:w-[min(560px,calc(100vw-2rem))] sm:-translate-x-1/2 sm:-translate-y-1/2"
        data-testid="reader-placement-manager"
      >
        <header className="flex items-center justify-between border-b border-border px-4 py-3">
          <div>
            <h2 className="text-sm font-semibold">Placements</h2>
            <p className="text-[11px] text-muted-foreground">
              One email can appear on multiple boards without duplicating the
              message.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-8 w-8 items-center justify-center rounded-md hover:bg-accent pointer-coarse:h-11 pointer-coarse:w-11"
            aria-label="Close Placement Manager"
          >
            <X className="h-4 w-4" />
          </button>
        </header>
        {searchable && (
          <div className="border-b border-border px-4 py-2">
            <div className="relative">
              <Search
                className="pointer-events-none absolute left-2 top-1/2 h-3 w-3 -translate-y-1/2 text-muted-foreground/60"
                aria-hidden
              />
              <input
                type="text"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Filter by board, lane, status, organisation…"
                aria-label="Filter placements"
                data-testid="placement-filter-input"
                className="w-full rounded border border-border bg-background py-1 pl-6 pr-2 text-[11px] focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
              />
            </div>
          </div>
        )}
        <div className="max-h-[55vh] overflow-y-auto p-3">
          {placements.length === 0 ? (
            <div className="rounded-lg border border-dashed border-border p-4 text-center text-xs text-muted-foreground">
              This email is not on a board yet.
            </div>
          ) : visible.length === 0 ? (
            <div
              className="p-4 text-center text-xs text-muted-foreground"
              data-testid="placement-filter-empty"
            >
              No placements match “{query.trim()}”.
            </div>
          ) : (
            <ol className="grid gap-2">
              {visible.map((placement) => (
                <PlacementRow
                  key={placement.allocation_id}
                  emailId={email.id}
                  placement={placement}
                  index={placements.indexOf(placement)}
                  api={api}
                  orgId={orgId}
                  organisationName={email.organisation_name}
                  isPrimary={placement === primary}
                  isDesignated={overrideId === placement.allocation_id}
                  changedElsewhere={changedIds.has(placement.allocation_id)}
                  busy={runner.isBusy(
                    `context:placements:${placement.allocation_id}`,
                  )}
                  onOpenCard={onOpenCard}
                  onOpenBoard={onOpenBoard}
                  onChanged={onChanged}
                  onDesignatePrimary={designatePrimary}
                  onRemoveStale={removeStale}
                  onRefresh={refreshRows}
                />
              ))}
            </ol>
          )}
        </div>
        <footer className="flex items-center justify-between gap-3 border-t border-border bg-muted/20 px-4 py-3">
          <span className="text-[10px] text-muted-foreground">
            Changes update this reader after the server confirms them.
          </span>
          <AllocateChip
            emailId={email.id}
            api={api}
            orgId={orgId}
            onAllocated={onAllocated}
            onConflict={(conflict) => {
              // 409 duplicate: open the EXISTING placement instead of
              // dead-ending (plan edge contract). The quick-allocate hook
              // already toasted the human-readable line.
              onOpenCard?.(conflict.card_id)
            }}
            label="Add placement"
            allowAdditional={placements.length > 0}
          />
        </footer>
      </section>
    </>
  )
}
